import { useIsFetching } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshControl } from 'react-native'
import { queryClient, type StoreKey } from '@/lib/pocketbase'

export function useCollectionRefresh(
    collections: StoreKey[],
    options?: { refreshOnMount?: boolean }
) {
    const [refreshing, setRefreshing] = useState(false)

    // biome-ignore lint/correctness/useExhaustiveDependencies: stable key from static collection names
    const stableNames = useMemo(() => collections, [collections.join(',')])

    const refresh = useCallback(async () => {
        await Promise.all(
            stableNames.map(name => queryClient.invalidateQueries({ queryKey: [name] }))
        )
    }, [stableNames])

    const handleRefresh = useCallback(async () => {
        setRefreshing(true)
        await refresh()
        setRefreshing(false)
    }, [refresh])

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
    useEffect(() => {
        if (options?.refreshOnMount) refresh()
    }, [])

    const fetchingCount = useIsFetching({
        predicate: query => stableNames.some(name => query.queryKey[0] === name),
    })
    const isSyncing = fetchingCount > 0 && !refreshing

    const refreshControl = <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />

    return { refreshing, handleRefresh, refreshControl, isSyncing }
}
